import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import { 
  Shield, 
  Plus, 
  CheckCircle2, 
  AlertTriangle,
  Clock,
  FileText,
  Eye,
  Download,
  Search,
  Calendar,
  User,
  Activity,
  BarChart3,
  Target,
  Award,
  ExternalLink,
  AlertCircle,
  Bookmark,
  Flag,
  Settings,
  Edit3,
  Trash2
} from 'lucide-react';

const API = `${BACKEND_URL}/api`;

const ComplianceAudit = ({ projectId, user }) => {
  const { t } = useTranslation();
  const [complianceItems, setComplianceItems] = useState([]);
  const [auditTrail, setAuditTrail] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [complianceStats, setComplianceStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [showComplianceDialog, setShowComplianceDialog] = useState(false);
  const [showCertificationDialog, setShowCertificationDialog] = useState(false);
  const [editingComplianceId, setEditingComplianceId] = useState(null);
  const [editingCertificationId, setEditingCertificationId] = useState(null);
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [approvingItem, setApprovingItem] = useState(null);
  const [approvalType, setApprovalType] = useState(null); // 'compliance' or 'certification'
  const [approvalNotes, setApprovalNotes] = useState('');

  const [complianceFormData, setComplianceFormData] = useState({
    requirement: '',
    description: '',
    regulatory_body: '',
    compliance_status: 'pending',
    due_date: '',
    priority: 'medium',
    evidence_required: '',
    assigned_to: ''
  });

  const [certificationFormData, setCertificationFormData] = useState({
    name: '',
    issuing_body: '',
    certification_type: 'quality',
    issue_date: '',
    expiry_date: '',
    status: 'active',
    certificate_url: ''
  });

  useEffect(() => {
    fetchComplianceData();
  }, [projectId]);

  const fetchComplianceData = async () => {
    try {
      const [complianceRes, auditRes, certRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}/compliance-items`),
        axios.get(`${API}/projects/${projectId}/audit-trail`),
        axios.get(`${API}/projects/${projectId}/certifications`)
      ]);
      
      setComplianceItems(complianceRes.data);
      setAuditTrail(auditRes.data);
      setCertifications(certRes.data);
      
      // Calculate compliance statistics
      const stats = calculateComplianceStats(complianceRes.data, certRes.data);
      setComplianceStats(stats);
      
    } catch (error) {
      toast.error(t('compliance_audit.load_failed'));
      console.error('Compliance data fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateComplianceStats = (items, certs) => {
    const total = items.length;
    const compliant = items.filter(item => item.compliance_status === 'compliant').length;
    const pending = items.filter(item => item.compliance_status === 'pending').length;
    const nonCompliant = items.filter(item => item.compliance_status === 'non_compliant').length;
    const overdue = items.filter(item => 
      new Date(item.due_date) < new Date() && item.compliance_status !== 'compliant'
    ).length;

    const activeCerts = certs.filter(cert => cert.status === 'active').length;
    const expiringSoon = certs.filter(cert => {
      const expiryDate = new Date(cert.expiry_date);
      const thirtyDaysFromNow = new Date();
      thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
      return expiryDate <= thirtyDaysFromNow && cert.status === 'active';
    }).length;

    return {
      totalCompliance: total,
      compliantPercentage: total > 0 ? ((compliant / total) * 100).toFixed(1) : 0,
      pendingItems: pending,
      nonCompliantItems: nonCompliant,
      overdueItems: overdue,
      activeCertifications: activeCerts,
      expiringSoonCerts: expiringSoon,
      complianceScore: total > 0 ? Math.round((compliant / total) * 100) : 0
    };
  };

  const handleCreateCompliance = async (e) => {
    e.preventDefault();
    try {
      const complianceData = {
        ...complianceFormData,
        due_date: new Date(complianceFormData.due_date).toISOString()
      };

      if (editingComplianceId) {
        // Update existing item
        await handleEditCompliance(editingComplianceId, complianceData);
      } else {
        // Create new item
        await axios.post(`${API}/projects/${projectId}/compliance-items`, complianceData);
        toast.success('Compliance item created successfully!');
        setShowComplianceDialog(false);
        setComplianceFormData({
          requirement: '',
          description: '',
          regulatory_body: '',
          compliance_status: 'pending',
          due_date: '',
          priority: 'medium',
          evidence_required: '',
          assigned_to: ''
        });
        fetchComplianceData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save compliance item');
    }
  };

  const handleCreateCertification = async (e) => {
    e.preventDefault();
    try {
      const certificationData = {
        ...certificationFormData,
        issue_date: new Date(certificationFormData.issue_date).toISOString(),
        expiry_date: new Date(certificationFormData.expiry_date).toISOString()
      };

      if (editingCertificationId) {
        // Update existing certification
        await handleEditCertification(editingCertificationId, certificationData);
      } else {
        // Create new certification
        await axios.post(`${API}/projects/${projectId}/certifications`, certificationData);
        toast.success('Certification added successfully!');
        setShowCertificationDialog(false);
        setCertificationFormData({
          name: '',
          issuing_body: '',
          certification_type: 'quality',
          issue_date: '',
          expiry_date: '',
          status: 'active',
          certificate_url: ''
        });
        fetchComplianceData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save certification');
    }
  };

  const updateComplianceStatus = async (itemId, status) => {
    try {
      await axios.patch(`${API}/projects/${projectId}/compliance-items/${itemId}`, {
        compliance_status: status
      });
      toast.success('Compliance status updated');
      fetchComplianceData();
    } catch (error) {
      toast.error('Failed to update compliance status');
    }
  };

  const handleEditCompliance = async (itemId, updatedData) => {
    try {
      await axios.put(`${API}/projects/${projectId}/compliance-items/${itemId}`, updatedData);
      const statusMsg = user.role === 'social_actor' ? 
        'Compliance item edit submitted for approval!' : 
        'Compliance item updated successfully!';
      toast.success(statusMsg);
      setShowComplianceDialog(false);
      setEditingComplianceId(null);
      setComplianceFormData({
        requirement: '',
        description: '',
        regulatory_body: '',
        compliance_status: 'pending',
        due_date: '',
        priority: 'medium',
        evidence_required: '',
        assigned_to: ''
      });
      fetchComplianceData();
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data?.detail || 'Failed to update compliance item';
      toast.error(errorMsg);
    }
  };

  const openEditComplianceDialog = (item) => {
    setEditingComplianceId(item.id);
    setComplianceFormData({
      requirement: item.requirement || '',
      description: item.description || '',
      regulatory_body: item.regulatory_body || '',
      compliance_status: item.compliance_status || 'pending',
      due_date: item.due_date ? item.due_date.split('T')[0] : '',
      priority: item.priority || 'medium',
      evidence_required: item.evidence_required || '',
      assigned_to: item.assigned_to || ''
    });
    setShowComplianceDialog(true);
  };

  const handleDeleteCompliance = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this compliance item?')) return;
    
    try {
      await axios.delete(`${API}/projects/${projectId}/compliance-items/${itemId}`);
      const statusMsg = user.role === 'social_actor' ? 
        'Compliance item deletion submitted for approval!' : 
        'Compliance item deleted successfully!';
      toast.success(statusMsg);
      fetchComplianceData();
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data?.detail || 'Failed to delete compliance item';
      toast.error(errorMsg);
    }
  };

  const handleEditCertification = async (certId, updatedData) => {
    try {
      await axios.put(`${API}/projects/${projectId}/certifications/${certId}`, updatedData);
      const statusMsg = user.role === 'social_actor' ? 
        'Certification edit submitted for approval!' : 
        'Certification updated successfully!';
      toast.success(statusMsg);
      setShowCertificationDialog(false);
      setEditingCertificationId(null);
      setCertificationFormData({
        name: '',
        issuing_body: '',
        certification_type: 'quality',
        issue_date: '',
        expiry_date: '',
        status: 'active',
        certificate_url: ''
      });
      fetchComplianceData();
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data?.detail || 'Failed to update certification';
      toast.error(errorMsg);
    }
  };

  const openEditCertificationDialog = (cert) => {
    setEditingCertificationId(cert.id);
    setCertificationFormData({
      name: cert.name || '',
      issuing_body: cert.issuing_body || '',
      certification_type: cert.certification_type || 'quality',
      issue_date: cert.issue_date ? cert.issue_date.split('T')[0] : '',
      expiry_date: cert.expiry_date ? cert.expiry_date.split('T')[0] : '',
      status: cert.status || 'active',
      certificate_url: cert.certificate_url || ''
    });
    setShowCertificationDialog(true);
  };

  const handleDeleteCertification = async (certId) => {
    if (!window.confirm('Are you sure you want to delete this certification?')) return;
    
    try {
      await axios.delete(`${API}/projects/${projectId}/certifications/${certId}`);
      const statusMsg = user.role === 'social_actor' ? 
        'Certification deletion submitted for approval!' : 
        'Certification deleted successfully!';
      toast.success(statusMsg);
      fetchComplianceData();
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data?.detail || 'Failed to delete certification';
      toast.error(errorMsg);
    }
  };

  const handleExportAuditLog = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/compliance/audit-log/export`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit_log_${projectId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Audit log exported successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to export audit log');
    }
  };

  const handleGenerateReport = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/compliance/report/export`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `compliance_report_${projectId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Compliance report generated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate report');
    }
  };

  const openApprovalDialog = (item, type) => {
    setApprovingItem(item);
    setApprovalType(type);
    setApprovalNotes('');
    setShowApprovalDialog(true);
  };

  const handleApprove = async (approved) => {
    if (!approvingItem) return;
    
    try {
      const endpoint = approvalType === 'compliance' 
        ? `${API}/projects/${projectId}/compliance-items/${approvingItem.id}/approve-edit`
        : `${API}/projects/${projectId}/certifications/${approvingItem.id}/approve-edit`;
      
      await axios.post(endpoint, {
        approved: approved,
        notes: approvalNotes
      });
      
      toast.success(approved ? 'Changes approved successfully!' : 'Changes rejected');
      setShowApprovalDialog(false);
      setApprovingItem(null);
      setApprovalNotes('');
      fetchComplianceData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process approval');
    }
  };

  const handleApproveDeletion = async (item, type, approved) => {
    try {
      const endpoint = type === 'compliance'
        ? `${API}/projects/${projectId}/compliance-items/${item.id}/approve-deletion`
        : `${API}/projects/${projectId}/certifications/${item.id}/approve-deletion`;
      
      await axios.post(endpoint, {
        approved: approved,
        notes: approvalNotes || ''
      });
      
      toast.success(approved ? 'Deletion approved' : 'Deletion rejected');
      fetchComplianceData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process deletion approval');
    }
  };

  const getEditStatusBadge = (item) => {
    if (item.edit_status === 'pending_approval') {
      return <Badge className="bg-yellow-100 text-yellow-800 ml-2">Pending Approval</Badge>;
    }
    if (item.edit_status === 'pending_deletion') {
      return <Badge className="bg-red-100 text-red-800 ml-2">Deletion Requested</Badge>;
    }
    return null;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'compliant': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'non_compliant': return 'bg-red-100 text-red-800';
      case 'under_review': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'bg-red-600';
      case 'high': return 'bg-orange-500';
      case 'medium': return 'bg-yellow-700';
      case 'low': return 'bg-green-700';
      default: return 'bg-gray-500';
    }
  };

  const MetricCard = ({ title, value, subtitle, icon: Icon, color = 'blue' }) => (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">{title}</p>
            <p className={`text-2xl font-bold text-${color}-600`}>{value}</p>
            {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-full bg-${color}-100`}>
            <Icon className={`h-5 w-5 text-${color}-600`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const filteredItems = complianceItems.filter(item => 
    selectedFilter === 'all' || item.compliance_status === selectedFilter
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold flex items-center space-x-2">
            <Shield className="h-5 w-5 text-blue-600" />
            <span>Compliance & Audit Module</span>
          </h3>
          <p className="text-sm text-gray-600">Regulatory compliance tracking, audit trails, and certifications</p>
        </div>
        <div className="flex items-center space-x-2">
          <Select value={selectedFilter} onValueChange={setSelectedFilter}>
            <SelectTrigger className="w-40" data-testid="compliance-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Items</SelectItem>
              <SelectItem value="compliant">Compliant</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="non_compliant">Non-Compliant</SelectItem>
              <SelectItem value="under_review">Under Review</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Compliance Overview */}
      {complianceStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            title="Compliance Score"
            value={`${complianceStats.complianceScore}%`}
            subtitle={`${complianceStats.totalCompliance} total items`}
            icon={Target}
            color="blue"
          />
          <MetricCard
            title="Pending Items"
            value={complianceStats.pendingItems}
            subtitle="Require attention"
            icon={Clock}
            color="yellow"
          />
          <MetricCard
            title="Overdue Items"
            value={complianceStats.overdueItems}
            subtitle="Past due date"
            icon={AlertTriangle}
            color="red"
          />
          <MetricCard
            title="Active Certifications"
            value={complianceStats.activeCertifications}
            subtitle={`${complianceStats.expiringSoonCerts} expiring soon`}
            icon={Award}
            color="green"
          />
        </div>
      )}

      {/* Main Content */}
      <Tabs defaultValue="compliance" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="compliance">{t('compliance_audit.tab_compliance')}</TabsTrigger>
          <TabsTrigger value="certifications">{t('compliance_audit.tab_certifications')}</TabsTrigger>
          <TabsTrigger value="audit">{t('compliance_audit.tab_audit')}</TabsTrigger>
          <TabsTrigger value="reports">{t('compliance_audit.tab_reports')}</TabsTrigger>
        </TabsList>

        {/* Compliance Items Tab */}
        <TabsContent value="compliance" className="space-y-6">
          <div className="flex justify-between items-center">
            <h4 className="text-lg font-semibold">{t('compliance_audit.compliance_management')}</h4>
            <Dialog open={showComplianceDialog} onOpenChange={setShowComplianceDialog}>
              <DialogTrigger asChild>
                <Button data-testid="add-compliance-item-button">
                  <Plus className="h-4 w-4 mr-2" />
                  {t('compliance_audit.add_compliance_item')}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>{editingComplianceId ? 'Edit Compliance Requirement' : 'Add Compliance Requirement'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateCompliance} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="requirement">Requirement</Label>
                      <Input
                        id="requirement"
                        value={complianceFormData.requirement}
                        onChange={(e) => setComplianceFormData({...complianceFormData, requirement: e.target.value})}
                        placeholder="e.g., Data Protection Compliance"
                        required
                        data-testid="compliance-requirement-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="regulatory-body">Regulatory Body</Label>
                      <Input
                        id="regulatory-body"
                        value={complianceFormData.regulatory_body}
                        onChange={(e) => setComplianceFormData({...complianceFormData, regulatory_body: e.target.value})}
                        placeholder="e.g., GDPR, SOX, ISO"
                        required
                        data-testid="compliance-regulatory-body-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="compliance-description">Description</Label>
                    <Textarea
                      id="compliance-description"
                      value={complianceFormData.description}
                      onChange={(e) => setComplianceFormData({...complianceFormData, description: e.target.value})}
                      placeholder="Detailed description of compliance requirement..."
                      rows={3}
                      required
                      data-testid="compliance-description-input"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="due-date">Due Date</Label>
                      <Input
                        id="due-date"
                        type="date"
                        value={complianceFormData.due_date}
                        onChange={(e) => setComplianceFormData({...complianceFormData, due_date: e.target.value})}
                        required
                        data-testid="compliance-due-date-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="priority">Priority</Label>
                      <Select value={complianceFormData.priority} onValueChange={(value) => 
                        setComplianceFormData({...complianceFormData, priority: value})
                      }>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="critical">Critical</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="assigned-to">Assigned To</Label>
                      <Input
                        id="assigned-to"
                        value={complianceFormData.assigned_to}
                        onChange={(e) => setComplianceFormData({...complianceFormData, assigned_to: e.target.value})}
                        placeholder="Team member"
                        data-testid="compliance-assigned-to-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="evidence-required">Evidence Required</Label>
                    <Textarea
                      id="evidence-required"
                      value={complianceFormData.evidence_required}
                      onChange={(e) => setComplianceFormData({...complianceFormData, evidence_required: e.target.value})}
                      placeholder="Documentation and evidence needed for compliance..."
                      rows={2}
                      data-testid="compliance-evidence-input"
                    />
                  </div>
                  <div className="flex justify-end space-x-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setShowComplianceDialog(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" data-testid="create-compliance-item-submit">
                      {editingComplianceId ? 'Update Compliance Item' : 'Add Compliance Item'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="space-y-4">
            {filteredItems.map((item) => {
              return (<Card key={item.id} data-testid={`compliance-item-${item.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4">
                      <div className={`w-3 h-3 rounded-full mt-2 ${getPriorityColor(item.priority)}`}></div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <h4 className="font-medium">{item.requirement}</h4>
                          <Badge className={getStatusColor(item.compliance_status)}>
                            {item.compliance_status ? item.compliance_status.replace('_', ' ') : 'N/A'}
                          </Badge>
                          {new Date(item.due_date) < new Date() && item.compliance_status !== 'compliant' && (
                            <Badge className="bg-red-100 text-red-800">
                              <AlertTriangle className="h-3 w-3 mr-1" />
                              Overdue
                            </Badge>
                          )}
                          {getEditStatusBadge(item)}
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{item.description}</p>
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <span>
                            <Shield className="h-4 w-4 inline mr-1" />
                            {item.regulatory_body}
                          </span>
                          <span>
                            <Calendar className="h-4 w-4 inline mr-1" />
                            Due: {new Date(item.due_date).toLocaleDateString('en-GB')}
                          </span>
                          {item.assigned_to && (
                            <span>
                              <User className="h-4 w-4 inline mr-1" />
                              {item.assigned_to}
                            </span>
                          )}
                        </div>
                        {item.evidence_required && (
                          <div className="mt-2 p-2 bg-gray-50 rounded text-sm">
                            <strong>Evidence Required:</strong> {item.evidence_required}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {/* Review button for PM/Admin when there are pending changes */}
                      {(item.edit_status === 'pending_approval' || item.edit_status === 'pending_deletion') && 
                       (user.role === 'project_manager' || user.role === 'administrator') && (
                        <Button
                          size="sm"
                          variant="default"
                          className="bg-blue-600 hover:bg-blue-700"
                          onClick={() => {
                            if (item.edit_status === 'pending_deletion') {
                              // Handle deletion approval directly
                              if (window.confirm(`Approve deletion of "${item.requirement}"?`)) {
                                handleApproveDeletion(item, 'compliance', true);
                              }
                            } else {
                              openApprovalDialog(item, 'compliance');
                            }
                          }}
                          data-testid={`review-compliance-edit-${item.id}`}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          Review {item.edit_status === 'pending_deletion' ? 'Deletion' : 'Changes'}
                        </Button>
                      )}
                      
                      {/* Edit/Delete buttons - hide when pending approval */}
                      {!item.edit_status && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEditComplianceDialog(item)}
                            data-testid={`edit-compliance-${item.id}`}
                            title="Edit compliance item"
                          >
                            <Edit3 className="h-4 w-4 mr-1" />
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteCompliance(item.id)}
                            data-testid={`delete-compliance-${item.id}`}
                            title="Delete compliance item"
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Delete
                          </Button>
                        </>
                      )}
                      
                      {item.compliance_status === 'pending' && user.role === 'administrator' && (
                        <>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => updateComplianceStatus(item.id, 'under_review')}
                            data-testid={`review-compliance-${item.id}`}
                          >
                            Review
                          </Button>
                          <Button 
                            size="sm"
                            onClick={() => updateComplianceStatus(item.id, 'compliant')}
                            data-testid={`approve-compliance-${item.id}`}
                          >
                            Mark Compliant
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
            })}
            {filteredItems.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <Shield className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No compliance items found</p>
                <p className="text-sm">Add compliance requirements to track regulatory adherence</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Certifications Tab */}
        <TabsContent value="certifications" className="space-y-6">
          <div className="flex justify-between items-center">
            <h4 className="text-lg font-semibold">Certifications Management</h4>
            <Dialog open={showCertificationDialog} onOpenChange={setShowCertificationDialog}>
              <DialogTrigger asChild>
                <Button data-testid="add-certification-button">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Certification
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingCertificationId ? 'Edit Certification' : 'Add Certification'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateCertification} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="cert-name">Certification Name</Label>
                      <Input
                        id="cert-name"
                        value={certificationFormData.name}
                        onChange={(e) => setCertificationFormData({...certificationFormData, name: e.target.value})}
                        placeholder="e.g., ISO 9001"
                        required
                        data-testid="certification-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="issuing-body">Issuing Body</Label>
                      <Input
                        id="issuing-body"
                        value={certificationFormData.issuing_body}
                        onChange={(e) => setCertificationFormData({...certificationFormData, issuing_body: e.target.value})}
                        placeholder="e.g., ISO Organization"
                        required
                        data-testid="certification-issuing-body-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cert-type">Certification Type</Label>
                    <Select value={certificationFormData.certification_type} onValueChange={(value) => 
                      setCertificationFormData({...certificationFormData, certification_type: value})
                    }>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="quality">Quality Management</SelectItem>
                        <SelectItem value="environmental">Environmental</SelectItem>
                        <SelectItem value="safety">Safety & Health</SelectItem>
                        <SelectItem value="security">Information Security</SelectItem>
                        <SelectItem value="social">Social Responsibility</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="issue-date">Issue Date</Label>
                      <Input
                        id="issue-date"
                        type="date"
                        value={certificationFormData.issue_date}
                        onChange={(e) => setCertificationFormData({...certificationFormData, issue_date: e.target.value})}
                        required
                        data-testid="certification-issue-date-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="expiry-date">Expiry Date</Label>
                      <Input
                        id="expiry-date"
                        type="date"
                        value={certificationFormData.expiry_date}
                        onChange={(e) => setCertificationFormData({...certificationFormData, expiry_date: e.target.value})}
                        required
                        data-testid="certification-expiry-date-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="certificate-url">Certificate URL</Label>
                    <Input
                      id="certificate-url"
                      type="url"
                      value={certificationFormData.certificate_url}
                      onChange={(e) => setCertificationFormData({...certificationFormData, certificate_url: e.target.value})}
                      placeholder="https://..."
                      data-testid="certification-url-input"
                    />
                  </div>
                  <div className="flex justify-end space-x-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setShowCertificationDialog(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" data-testid="create-certification-submit">
                      {editingCertificationId ? 'Update Certification' : 'Add Certification'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {certifications.map((cert) => {
              const isExpiringSoon = new Date(cert.expiry_date) <= new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
              
              return (
                <Card key={cert.id} className={`${isExpiringSoon ? 'border-yellow-300' : ''}`} data-testid={`certification-card-${cert.id}`}>
                  <CardContent className="p-4">
                    <div className="space-y-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center space-x-2">
                          <Award className="h-5 w-5 text-blue-600" />
                          <h4 className="font-medium">{cert.name}</h4>
                          {getEditStatusBadge(cert)}
                        </div>
                        <Badge className={cert.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                          {cert.status}
                        </Badge>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Issuing Body:</span>
                          <span className="font-medium">{cert.issuing_body}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Type:</span>
                          <span className="font-medium capitalize">{cert.certification_type ? cert.certification_type.replace('_', ' ') : 'N/A'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Issue Date:</span>
                          <span className="font-medium">{new Date(cert.issue_date).toLocaleDateString('en-GB')}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Expiry Date:</span>
                          <span className={`font-medium ${isExpiringSoon ? 'text-yellow-700' : ''}`}>
                            {new Date(cert.expiry_date).toLocaleDateString('en-GB')}
                          </span>
                        </div>
                      </div>
                      {isExpiringSoon && (
                        <div className="p-2 bg-yellow-50 border border-yellow-200 rounded text-sm">
                          <AlertTriangle className="h-4 w-4 inline text-yellow-700 mr-1" />
                          <span className="text-yellow-800">Expires within 30 days</span>
                        </div>
                      )}
                      <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                        <div className="flex items-center space-x-2">
                          {cert.certificate_url && (
                            <Button size="sm" variant="outline" asChild>
                              <a href={cert.certificate_url} target="_blank" rel="noopener noreferrer">
                                <ExternalLink className="h-4 w-4 mr-1" />
                                View
                              </a>
                            </Button>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          {/* Review button for PM/Admin when there are pending changes */}
                          {(cert.edit_status === 'pending_approval' || cert.edit_status === 'pending_deletion') && 
                           (user.role === 'project_manager' || user.role === 'administrator') && (
                            <Button
                              size="sm"
                              variant="default"
                              className="bg-blue-600 hover:bg-blue-700"
                              onClick={() => {
                                if (cert.edit_status === 'pending_deletion') {
                                  if (window.confirm(`Approve deletion of "${cert.name}"?`)) {
                                    handleApproveDeletion(cert, 'certification', true);
                                  }
                                } else {
                                  openApprovalDialog(cert, 'certification');
                                }
                              }}
                              data-testid={`review-certification-edit-${cert.id}`}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Review {cert.edit_status === 'pending_deletion' ? 'Deletion' : 'Changes'}
                            </Button>
                          )}
                          
                          {/* Edit/Delete buttons - hide when pending approval */}
                          {!cert.edit_status && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openEditCertificationDialog(cert)}
                                data-testid={`edit-certification-${cert.id}`}
                                title="Edit certification"
                              >
                                <Edit3 className="h-4 w-4 mr-1" />
                                Edit
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDeleteCertification(cert.id)}
                                data-testid={`delete-certification-${cert.id}`}
                                title="Delete certification"
                              >
                                <Trash2 className="h-4 w-4 mr-1" />
                                Delete
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            {certifications.length === 0 && (
              <div className="col-span-full text-center py-8 text-gray-500">
                <Award className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No certifications added yet</p>
                <p className="text-sm">Add certifications to track compliance credentials</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Audit Trail Tab */}
        <TabsContent value="audit" className="space-y-6">
          <div className="flex justify-between items-center">
            <h4 className="text-lg font-semibold">Audit Trail</h4>
            <Button variant="outline" data-testid="export-audit-trail" onClick={handleExportAuditLog}>
              <Download className="h-4 w-4 mr-2" />
              Export Audit Log
            </Button>
          </div>

          <div className="space-y-3">
            {auditTrail.map((entry) => (
              <Card key={entry.id} data-testid={`audit-entry-${entry.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <Activity className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="font-medium">{entry.action}</h4>
                        <span className="text-sm text-gray-500">
                          {new Date(entry.timestamp).toLocaleString('en-GB')}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{entry.description}</p>
                      <div className="flex items-center space-x-4 text-xs text-gray-500">
                        <span>
                          <User className="h-3 w-3 inline mr-1" />
                          {entry.performed_by}
                        </span>
                        <span>
                          <FileText className="h-3 w-3 inline mr-1" />
                          {entry.resource_type}
                        </span>
                        {entry.ip_address && (
                          <span>IP: {entry.ip_address}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {auditTrail.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <Activity className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No audit entries found</p>
                <p className="text-sm">System activities will be logged here</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports" className="space-y-6">
          <div className="flex justify-between items-center">
            <h4 className="text-lg font-semibold">Compliance Reports</h4>
            <Button data-testid="generate-compliance-report" onClick={handleGenerateReport}>
              <FileText className="h-4 w-4 mr-2" />
              Generate Report
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Compliance Overview */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <BarChart3 className="h-5 w-5 text-blue-600" />
                  <span>Compliance Overview</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-blue-600">
                      {complianceStats.complianceScore}%
                    </div>
                    <p className="text-sm text-gray-600">Overall Compliance Score</p>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Compliant Items:</span>
                      <span className="font-medium text-green-700">
                        {complianceItems.filter(i => i.compliance_status === 'compliant').length}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Pending Items:</span>
                      <span className="font-medium text-yellow-700">{complianceStats.pendingItems}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Non-Compliant:</span>
                      <span className="font-medium text-red-600">{complianceStats.nonCompliantItems}</span>
                    </div>
                  </div>
                  <Progress value={complianceStats.complianceScore} className="h-3" />
                </div>
              </CardContent>
            </Card>

            {/* Certification Status */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Award className="h-5 w-5 text-green-700" />
                  <span>Certification Status</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-green-700">
                        {complianceStats.activeCertifications}
                      </div>
                      <p className="text-sm text-gray-600">Active Certifications</p>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-yellow-700">
                        {complianceStats.expiringSoonCerts}
                      </div>
                      <p className="text-sm text-gray-600">Expiring Soon</p>
                    </div>
                  </div>
                  {complianceStats.expiringSoonCerts > 0 && (
                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded">
                      <AlertTriangle className="h-4 w-4 inline text-yellow-700 mr-2" />
                      <span className="text-sm text-yellow-800">
                        {complianceStats.expiringSoonCerts} certification(s) expiring within 30 days
                      </span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Compliance Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Shield className="h-5 w-5 text-purple-600" />
                <span>Compliance Summary</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600">
                    {complianceStats.totalCompliance}
                  </div>
                  <p className="text-sm text-gray-600">Total Requirements</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-700">
                    {complianceItems.filter(i => i.compliance_status === 'compliant').length}
                  </div>
                  <p className="text-sm text-gray-600">Compliant</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-yellow-700">
                    {complianceStats.pendingItems}
                  </div>
                  <p className="text-sm text-gray-600">Pending Review</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-red-600">
                    {complianceStats.overdueItems}
                  </div>
                  <p className="text-sm text-gray-600">Overdue Items</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Approval Dialog */}
      <Dialog open={showApprovalDialog} onOpenChange={setShowApprovalDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Review {approvalType === 'compliance' ? 'Compliance Item' : 'Certification'} Changes</DialogTitle>
          </DialogHeader>
          
          {approvingItem && approvingItem.pending_changes && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <h4 className="font-semibold text-sm text-gray-700">Current Values</h4>
                  <div className="bg-red-50 border border-red-200 rounded p-3 text-sm space-y-1">
                    {Object.entries(approvingItem).map(([key, value]) => {
                      if (!['id', 'project_id', 'edit_status', 'pending_changes', '_id', 'created_at', 'updated_at', 'updated_by'].includes(key)) {
                        return (
                          <div key={key}>
                            <span className="font-medium capitalize">{key.replace(/_/g, ' ')}:</span>{' '}
                            <span>{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
                          </div>
                        );
                      }
                      return null;
                    })}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <h4 className="font-semibold text-sm text-gray-700">Proposed Changes</h4>
                  <div className="bg-green-50 border border-green-200 rounded p-3 text-sm space-y-1">
                    {Object.entries(approvingItem.pending_changes).map(([key, value]) => (
                      <div key={key}>
                        <span className="font-medium capitalize">{key.replace(/_/g, ' ')}:</span>{' '}
                        <span>{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="approval-notes">Review Notes (Optional)</Label>
                <textarea
                  id="approval-notes"
                  className="w-full border rounded p-2 min-h-[80px]"
                  value={approvalNotes}
                  onChange={(e) => setApprovalNotes(e.target.value)}
                  placeholder="Add notes about your decision..."
                />
              </div>

              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  onClick={() => setShowApprovalDialog(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="outline"
                  className="text-red-600 border-red-300 hover:bg-red-50"
                  onClick={() => handleApprove(false)}
                >
                  Reject
                </Button>
                <Button
                  className="bg-green-700 hover:bg-green-800"
                  onClick={() => handleApprove(true)}
                >
                  Approve
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ComplianceAudit;